import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiBell, FiCheck, FiCheckCircle, FiX } from 'react-icons/fi';
import {
  getActiveNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  AppNotification,
} from '../../lib/notifications';
import './NotificationBell.css';

function getSessionAccountOwnerId(): number {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return 0;
    const user = JSON.parse(raw);
    const userId = Number(user.user_id || user.id);
    // eslint-disable-next-line eqeqeq
    if (user.owner_id == null) return userId;
    return Number(user.owner_id);
  } catch { return 0; }
}

const NotificationBell: React.FC = () => {
  const accountOwnerId = useMemo(() => getSessionAccountOwnerId(), []);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    setNotifications(getActiveNotifications(accountOwnerId));
    setUnread(getUnreadCount(accountOwnerId));
  }, [accountOwnerId]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(prev => !prev);
    if (!open) refresh();
  };

  const handleMarkRead = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead(id);
    refresh();
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllAsRead(accountOwnerId);
    refresh();
  };

  const formatTime = (iso: string): string => {
    const d = new Date(iso.replace(' ', 'T'));
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="nb-container" ref={panelRef}>
      <button className="nb-bell-btn" onClick={handleToggle} title="Notifications">
        <FiBell size={20} />
        {unread > 0 && (
          <span className="nb-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="nb-panel">
          <div className="nb-panel-header">
            <span className="nb-panel-title">Notifications</span>
            {unread > 0 && (
              <button className="nb-mark-all" onClick={handleMarkAllRead}>
                <FiCheckCircle size={14} /> Mark all read
              </button>
            )}
            <button className="nb-close" onClick={() => setOpen(false)}>
              <FiX size={16} />
            </button>
          </div>

          <div className="nb-panel-body">
            {notifications.length === 0 ? (
              <div className="nb-empty">No notifications this week.</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.notification_id}
                  className={`nb-item ${n.is_read ? '' : 'nb-unread'}`}
                >
                  <div className="nb-item-content">
                    <div className="nb-item-title">{n.title}</div>
                    <div className="nb-item-message">{n.message}</div>
                    <div className="nb-item-meta">
                      <span className="nb-item-actor">{n.actor_name}</span>
                      <span className="nb-item-time">{formatTime(n.created_at)}</span>
                    </div>
                  </div>
                  {!n.is_read && (
                    <button
                      className="nb-item-read-btn"
                      onClick={(e) => handleMarkRead(n.notification_id, e)}
                      title="Mark as read"
                    >
                      <FiCheck size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="nb-panel-footer">
              Notifications older than 7 days are automatically archived.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
